import React, { useMemo, useState } from 'react';
import './CosmicSnapshot.css';

const PLANET_ICONS = {
  'Sun': '☉', 'Moon': '☽', 'Mars': '♂', 'Mercury': '☿',
  'Jupiter': '♃', 'Venus': '♀', 'Saturn': '♄', 'Rahu': '☊', 'Ketu': '☋'
};

const SIGN_TO_NUMBER = {
  'Aries': 1, 'Taurus': 2, 'Gemini': 3, 'Cancer': 4, 'Leo': 5, 'Virgo': 6,
  'Libra': 7, 'Scorpio': 8, 'Sagittarius': 9, 'Capricorn': 10, 'Aquarius': 11, 'Pisces': 12
};

const PLANET_PRIMARY_SIGN = {
  'Sun': 'Leo', 'Moon': 'Cancer', 'Mars': 'Aries', 'Mercury': 'Virgo',
  'Jupiter': 'Sagittarius', 'Venus': 'Libra', 'Saturn': 'Capricorn', 'Rahu': null, 'Ketu': null
};

const HOUSE_TO_FOCUS = {
  1: 'SELF', 2: 'WEALTH', 3: 'SKILLS', 4: 'HOME', 5: 'CREATIVITY',
  6: 'WORK', 7: 'RELATIONS', 8: 'TRANSFORM', 9: 'FORTUNE',
  10: 'CAREER', 11: 'GAINS', 12: 'RELEASE'
};

const PLANET_BENEFIC_SCORE = {
  'Jupiter': 2, 'Venus': 1.5, 'Moon': 1, 'Mercury': 0.5, 'Sun': 0,
  'Mars': -1, 'Saturn': -1, 'Rahu': -1, 'Ketu': -1
};

const getPlanetImpactWord = (planetName, houseNum) => {
  const lookupTable = {
    'Jupiter': { kendra_trikona: 'GROWTH', upachaya: 'EXPANSION', dusthana: 'PROTECTION', default: 'WISDOM' },
    'Venus': { kendra_trikona: 'HARMONY', upachaya: 'ABUNDANCE', dusthana: 'INDULGENCE', default: 'CREATIVITY' },
    'Mercury': { kendra_trikona: 'CLARITY', upachaya: 'ANALYSIS', dusthana: 'CONFUSION', default: 'COMMUNICATION' },
    'Moon': { kendra_trikona: 'INTUITION', upachaya: 'ADAPTATION', dusthana: 'FLUCTUATION', default: 'CARE' },
    'Sun': { kendra_trikona: 'AUTHORITY', upachaya: 'ASSERTION', dusthana: 'EGO', default: 'RECOGNITION' },
    'Mars': { kendra_trikona: 'DRIVE', upachaya: 'ACTION', dusthana: 'CONFLICT', default: 'AMBITION' },
    'Saturn': { kendra_trikona: 'RESTRICTION', upachaya: 'DISCIPLINE', dusthana: 'RELEASE', default: 'STRUCTURE' },
    'Rahu': { kendra_trikona: 'DISRUPTION', upachaya: 'AMBITION', dusthana: 'OBSESSION', default: 'INNOVATION' },
    'Ketu': { kendra_trikona: 'DETACHMENT', upachaya: 'RELEASE', dusthana: 'LIBERATION', default: 'WISDOM' }
  };
  const planetLookup = lookupTable[planetName] || lookupTable['Sun'];
  if ([1, 4, 7, 10, 5, 9].includes(houseNum)) return planetLookup.kendra_trikona;
  if ([3, 6, 10, 11].includes(houseNum)) return planetLookup.upachaya;
  if ([6, 8, 12].includes(houseNum)) return planetLookup.dusthana;
  return planetLookup.default;
};

const getModeWord = (planetName, score) => {
  const scoreType = score > 0.5 ? 'positive' : 'negative';
  const modeTable = {
    'Jupiter': scoreType === 'positive' ? 'LAUNCH' : 'EXPAND',
    'Venus': scoreType === 'positive' ? 'CONNECT' : 'CREATE',
    'Mercury': scoreType === 'positive' ? 'COMMUNICATE' : 'ANALYZE',
    'Moon': 'FEEL', 'Sun': scoreType === 'positive' ? 'LEAD' : 'ASSERT',
    'Mars': scoreType === 'positive' ? 'EXECUTE' : 'PAUSE',
    'Saturn': scoreType === 'positive' ? 'BUILD' : 'WAIT', 'Rahu': 'INNOVATE', 'Ketu': 'REFLECT'
  };
  return modeTable[planetName] || 'ACT';
};

const getBeneficScore = (planetName, houseNum, isRetro = false) => {
  let score = PLANET_BENEFIC_SCORE[planetName] || 0;
  if ([1, 4, 7, 10].includes(houseNum)) score += 1;
  else if ([5, 9].includes(houseNum)) score += 1;
  else if ([3, 6, 11].includes(houseNum)) score += 0.5;
  else if ([6, 8, 12].includes(houseNum)) score -= 1;
  if (houseNum === 8) score -= 2;
  if (isRetro) score += (score > 0 ? 0.5 : -0.5);
  return score;
};

const getEnergyWord = (score, houseNum) => {
  if (houseNum === 8) return 'TRANSFORMING';
  if (houseNum === 12) return 'RELEASING';
  if (score >= 2) return 'THRIVING';
  if (score >= 1) return 'RISING';
  if (score >= 0.5) return 'STABLE';
  if (score >= 0) return 'CAUTION';
  if (score >= -1) return 'FRICTION';
  return 'BLOCKED';
};

const getEnergyType = (score, houseNum) => {
  if (houseNum === 8 || houseNum === 12) return 'special';
  if (score >= 0.5) return 'positive';
  if (score > -1) return 'neutral';
  return 'negative';
};

const getPrimaryRuledHouse = (planetName, ascendantSign) => {
  const primarySign = PLANET_PRIMARY_SIGN[planetName];
  if (!primarySign) return null;
  const ascNum = SIGN_TO_NUMBER[ascendantSign] || 1;
  const planetSignNum = SIGN_TO_NUMBER[primarySign] || 1;
  return ((planetSignNum - ascNum + 12) % 12) + 1;
};

const getPlanetHouseInD1 = (planetName, planetaryPositions) => {
  const planet = planetaryPositions?.find(p => p.name === planetName);
  return planet?.house || null;
};

const getCyclePhase = (startDate, endDate) => {
  if (!startDate || !endDate) return 'ACTIVE';
  const start = startDate?._seconds ? new Date(startDate._seconds * 1000) : new Date(startDate);
  const end = endDate?._seconds ? new Date(endDate._seconds * 1000) : new Date(endDate);
  const now = new Date();
  const progress = (now - start) / (end - start);
  if (progress < 0.25) return 'BEGINNING';
  if (progress < 0.65) return 'PEAK';
  return 'CLOSING';
};

const computeSnapshot = (user) => {
  try {
    if (!user?.dasha_data?.current_dasha || !user?.astro_data?.astro_details || !user?.astro_data?.planetary_positions) {
      return null;
    }

    const dasha = user.dasha_data.current_dasha;
    const ascendant = user.astro_data.astro_details.ascendant;
    const planets = user.astro_data.planetary_positions;

    const mahadashaLord = dasha.major?.planet;
    const antardashaLord = dasha.minor?.planet;
    const pratyantardashaLord = dasha.sub_minor?.planet;

    if (!antardashaLord) return null;

    const focusHouse = getPrimaryRuledHouse(antardashaLord, ascendant) || getPlanetHouseInD1(antardashaLord, planets) || 1;
    const focusArea = HOUSE_TO_FOCUS[focusHouse] || 'SELF';

    const antardashaHouse = getPlanetHouseInD1(antardashaLord, planets) || focusHouse;
    const antardashaRetro = planets?.find(p => p.name === antardashaLord)?.isRetro || false;
    const score = getBeneficScore(antardashaLord, antardashaHouse, antardashaRetro);
    const energy = getEnergyWord(score, antardashaHouse);
    const energyType = getEnergyType(score, antardashaHouse);
    const mode = getModeWord(antardashaLord, score);
    const phase = getCyclePhase(dasha.minor?.start, dasha.minor?.end);

    const activePlanets = [];
    if (mahadashaLord) {
      const mahaHouse = getPlanetHouseInD1(mahadashaLord, planets) || 1;
      activePlanets.push({
        planet: mahadashaLord,
        impact: getPlanetImpactWord(mahadashaLord, mahaHouse),
        icon: PLANET_ICONS[mahadashaLord] || '●'
      });
    }
    if (antardashaLord) {
      const antarHouse = antardashaHouse;
      activePlanets.push({
        planet: antardashaLord,
        impact: getPlanetImpactWord(antardashaLord, antarHouse),
        icon: PLANET_ICONS[antardashaLord] || '●'
      });
    }
    if (pratyantardashaLord) {
      const pratyHouse = getPlanetHouseInD1(pratyantardashaLord, planets) || 1;
      activePlanets.push({
        planet: pratyantardashaLord,
        impact: getPlanetImpactWord(pratyantardashaLord, pratyHouse),
        icon: PLANET_ICONS[pratyantardashaLord] || '●'
      });
    }

    return { focusArea, energy, energyType, mode, phase, activePlanets };
  } catch (error) {
    console.error('Error computing cosmic snapshot:', error);
    return null;
  }
};

const getFocusIcon = (focusArea) => {
  const icons = {
    'SELF': '🧿', 'WEALTH': '💰', 'SKILLS': '⚡', 'HOME': '🏠', 'CREATIVITY': '🎨',
    'WORK': '⚙️', 'RELATIONS': '💕', 'TRANSFORM': '🔮', 'FORTUNE': '🌟',
    'CAREER': '🚀', 'GAINS': '📈', 'RELEASE': '🌸'
  };
  return icons[focusArea] || '⭐';
};

const getModeIcon = (mode) => {
  const icons = {
    'LAUNCH': '🚀', 'EXPAND': '📈', 'CONNECT': '🔗', 'CREATE': '✨',
    'COMMUNICATE': '💬', 'ANALYZE': '🔍', 'FEEL': '💫', 'LEAD': '👑',
    'ASSERT': '⚔️', 'EXECUTE': '⚡', 'PAUSE': '⏸', 'BUILD': '🔨',
    'INNOVATE': '💡', 'REFLECT': '🧘', 'WAIT': '⏳', 'ACT': '⭐'
  };
  return icons[mode] || '⭐';
};

const getEnergyDot = (type) => {
  const dots = {
    'positive': '🟢', 'negative': '🔴', 'neutral': '🟡', 'special': '🟣'
  };
  return dots[type] || '⚪';
};

const getDetailContent = (type, value, snapshot, user) => {
  const dasha = user?.dasha_data?.current_dasha;

  const details = {
    focus: {
      title: value,
      icon: getFocusIcon(value),
      explanation: {
        'SELF': 'You are in a phase of self-discovery and personal identity. Focus on your own growth.',
        'WEALTH': 'Financial matters and family resources are central. Opportunities for prosperity.',
        'SKILLS': 'Communication and learning are highlighted. A time for acquiring new abilities.',
        'HOME': 'Home, family, and emotional peace take priority. Inner security matters.',
        'CREATIVITY': 'Expression and creation are at the forefront. Ideal for artistic pursuits.',
        'WORK': 'Service, work obligations, and health challenges emerge. Stay disciplined.',
        'RELATIONS': 'Partnerships and relationships dominate. Both romantic and business connections.',
        'TRANSFORM': 'Deep transformation and hidden matters surface. A time of profound change.',
        'FORTUNE': 'Luck and dharma align. Higher wisdom and spiritual growth are favored.',
        'CAREER': 'Professional status and public reputation are active. Career advancement possible.',
        'GAINS': 'Abundance, friendships, and ambitions flourish. Social networks matter.',
        'RELEASE': 'Spirituality and letting go are themes. Foreign travel and expenses increase.'
      },
      advice: {
        'SELF': 'Invest in personal development. This is YOUR time to focus inward.',
        'WEALTH': 'Make financial moves. Wealth creation is favored during this period.',
        'SKILLS': 'Learn, teach, communicate. Share your knowledge and expand your capabilities.',
        'HOME': 'Strengthen family bonds. Create a secure foundation.',
        'CREATIVITY': 'Create and express. Share your art with the world.',
        'WORK': 'Execute your tasks with precision. Health routines matter.',
        'RELATIONS': 'Nurture partnerships. Make commitments if timing feels right.',
        'TRANSFORM': 'Embrace change. Release what no longer serves you.',
        'FORTUNE': 'Trust your intuition. Higher forces are supporting you.',
        'CAREER': 'Go for that promotion or big career move. Visibility is high.',
        'GAINS': 'Expand your network. Collaborate and build alliances.',
        'RELEASE': 'Meditate and reflect. This is a time for spiritual work.'
      },
      technical: `Antardasha Lord: ${dasha?.minor?.planet || 'Unknown'}`
    },
    energy: {
      title: snapshot.energy,
      icon: getEnergyDot(snapshot.energyType),
      explanation: {
        'THRIVING': 'All systems aligned. Maximum cosmic support.',
        'RISING': 'Momentum is building. Conditions favor growth.',
        'STABLE': 'Steady state. Maintenance and consolidation phase.',
        'CAUTION': 'Mixed energies. Proceed with awareness.',
        'FRICTION': 'Challenges present. Resilience required.',
        'BLOCKED': 'Major obstacles. Patience and acceptance needed.',
        'TRANSFORMING': 'Deep change in progress. Trust the process.',
        'RELEASING': 'Letting go phase. Spiritual evolution.'
      },
      advice: {
        'THRIVING': 'Seize this moment. Make bold moves. This is your window.',
        'RISING': 'Push forward. Build momentum toward your goals.',
        'STABLE': 'Consolidate gains. Strengthen what you\'ve built.',
        'CAUTION': 'Act with intention. Double-check decisions.',
        'FRICTION': 'Use obstacles as teachers. Move slowly and deliberately.',
        'BLOCKED': 'Rest and reflect. This too shall pass.',
        'TRANSFORMING': 'Release control. Let life reshape you.',
        'RELEASING': 'Surrender. What\'s meant for you will return.'
      },
      technical: `House Position: ${snapshot.energyType}. Benefic Score indicates ${snapshot.energyType === 'positive' ? 'favorable' : 'challenging'} conditions.`
    },
    mode: {
      title: snapshot.mode,
      icon: getModeIcon(snapshot.mode),
      explanation: {
        'LAUNCH': 'The cosmos supports new beginnings. Initiate projects now.',
        'EXPAND': 'Growth energy is present. Scale what works.',
        'CONNECT': 'Relationship energy peaks. Collaborate and partner.',
        'CREATE': 'Creative forces flow. Make something new.',
        'COMMUNICATE': 'Words carry power. Speak, write, share.',
        'ANALYZE': 'Clarity and detail matter. Study and understand deeply.',
        'FEEL': 'Emotions guide the way. Listen to your intuition.',
        'LEAD': 'Authority energy is strong. Take charge.',
        'ASSERT': 'Stand firm in your convictions. Defend your position.',
        'EXECUTE': 'The time for action is now. Move decisively.',
        'PAUSE': 'Hold back. Let things unfold naturally.',
        'BUILD': 'Lay foundations. Construct with care and precision.',
        'INNOVATE': 'Break the mold. Experiment and disrupt.',
        'REFLECT': 'Look inward. Meditate on deeper truths.',
        'WAIT': 'Timing isn\'t ripe. Practice patience.',
        'ACT': 'Movement is needed. Don\'t overthink.'
      },
      advice: `${snapshot.mode === 'EXECUTE' ? 'Don\'t hesitate. Act now while energy supports you.' : snapshot.mode === 'PAUSE' ? 'Patience is your strength. Let others move first.' : snapshot.mode === 'WAIT' ? 'This isn\'t the moment. Prepare and prepare.' : snapshot.mode === 'LAUNCH' ? 'Initiate boldly. The cosmos has your back.' : snapshot.mode === 'REFLECT' ? 'Turn inward before outward action.' : 'Align your actions with this energy for maximum impact.'}`,
      technical: `Derived from ${dasha?.minor?.planet || 'Unknown'} in ${snapshot.energyType} state.`
    }
  };

  return details[type] || {};
};

const DetailsModal = ({ isOpen, type, value, snapshot, user, onClose }) => {
  if (!isOpen) return null;

  const content = getDetailContent(type, value, snapshot, user);

  return (
    <div className="cosmic-modal-overlay" onClick={onClose}>
      <div className="cosmic-modal-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="modal-close" onClick={onClose}>✕</div>

        <div className="modal-header">
          <div className="modal-icon">{content.icon}</div>
          <h2 className="modal-title">{content.title}</h2>
        </div>

        <div className="modal-content">
          <div className="modal-section">
            <h3 className="modal-section-title">What This Means</h3>
            <p className="modal-text">{content.explanation?.[value] || 'Cosmic influence in progress.'}</p>
          </div>

          <div className="modal-section">
            <h3 className="modal-section-title">Your Action</h3>
            <p className="modal-text modal-advice">{content.advice?.[value] || 'Move with awareness.'}</p>
          </div>

          <div className="modal-section">
            <h3 className="modal-section-title">Technical Details</h3>
            <p className="modal-text modal-technical">{content.technical}</p>
          </div>
        </div>

        <div className="modal-footer">
          <button className="modal-button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

const CosmicSnapshot = ({ user }) => {
  const snapshot = useMemo(() => computeSnapshot(user), [user]);
  const [modal, setModal] = useState({ isOpen: false, type: null, value: null });

  if (!snapshot) return null;

  const openModal = (type, value) => setModal({ isOpen: true, type, value });
  const closeModal = () => setModal({ isOpen: false, type: null, value: null });

  const getExpiryDate = () => {
    try {
      const endDate = user?.dasha_data?.current_dasha?.minor?.end;
      if (!endDate) return null;

      let end;
      if (endDate?._seconds) {
        end = new Date(endDate._seconds * 1000);
      } else if (typeof endDate === 'string') {
        end = new Date(endDate);
      } else if (endDate instanceof Date) {
        end = endDate;
      } else {
        return null;
      }

      if (isNaN(end.getTime())) return null;

      const now = new Date();
      const daysLeft = Math.ceil((end - now) / (1000 * 60 * 60 * 24));

      if (daysLeft < 0) return null;

      const monthsLeft = Math.floor(daysLeft / 30);
      const yearsLeft = Math.floor(monthsLeft / 12);

      let expiryText = '';
      if (yearsLeft > 0) {
        expiryText = `${yearsLeft}y ${monthsLeft % 12}mo`;
      } else if (monthsLeft > 0) {
        expiryText = `${monthsLeft}mo`;
      } else {
        expiryText = `${Math.max(daysLeft, 1)}d`;
      }

      const formattedDate = end.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      return { expiryText, endDate: formattedDate };
    } catch (error) {
      console.error('Error calculating expiry date:', error);
      return null;
    }
  };

  const expiry = getExpiryDate();

  return (
    <>
      <div className={`cosmic-snapshot-widget energy-${snapshot.energyType}`}>
        {/* Expiry Badge */}
        {expiry && (
          <div className="snapshot-expiry">
            <span className="expiry-icon">⏰</span>
            <span className="expiry-text">Until {expiry.endDate}</span>
            <span className="expiry-duration">({expiry.expiryText})</span>
          </div>
        )}

        {/* Top Row: Focus, Energy, Mode */}
        <div className="snapshot-grid">
          {/* Focus */}
          <div
            className="snapshot-item focus-item"
            onClick={() => openModal('focus', snapshot.focusArea)}
            role="button"
            tabIndex="0"
          >
            <div className="item-icon">{getFocusIcon(snapshot.focusArea)}</div>
            <div className="item-content">
              <div className="item-label">FOCUS</div>
              <div className="item-value">{snapshot.focusArea}</div>
            </div>
          </div>

          {/* Energy */}
          <div
            className="snapshot-item energy-item"
            onClick={() => openModal('energy', snapshot.energy)}
            role="button"
            tabIndex="0"
          >
            <div className="item-icon energy-dot">{getEnergyDot(snapshot.energyType)}</div>
            <div className="item-content">
              <div className="item-label">ENERGY</div>
              <div className={`item-value energy-text energy-${snapshot.energyType}`}>{snapshot.energy}</div>
            </div>
          </div>

          {/* Mode */}
          <div
            className="snapshot-item mode-item"
            onClick={() => openModal('mode', snapshot.mode)}
            role="button"
            tabIndex="0"
          >
            <div className="item-icon">{getModeIcon(snapshot.mode)}</div>
            <div className="item-content">
              <div className="item-label">MODE</div>
              <div className="item-value">{snapshot.mode}</div>
            </div>
          </div>
        </div>

        {/* Bottom Row: Active Forces + Phase */}
        <div className="snapshot-bottom">
          <div className="forces-section">
            <div className="forces-header">⚡ ACTIVE</div>
            <div className="forces-compact">
              {snapshot.activePlanets.map((p, idx) => (
                <div key={idx} className="force-badge">
                  <span className="badge-icon">{p.icon}</span>
                  <span className="badge-impact">{p.impact}</span>
                </div>
              ))}
            </div>
          </div>

          {snapshot.phase === 'PEAK' && (
            <div className="phase-section">
              <div className="phase-badge">{snapshot.phase}</div>
            </div>
          )}
        </div>
      </div>

      <DetailsModal
        isOpen={modal.isOpen}
        type={modal.type}
        value={modal.value}
        snapshot={snapshot}
        user={user}
        onClose={closeModal}
      />
    </>
  );
};

export default CosmicSnapshot;
